// Components==============
import DreamSelectModal from 'components/UI/DreamSelectModal/DreamSelectModal';
import GridFiller from 'components/UI/GridFiller/GridFiller';
import { AnimatePresence } from 'framer-motion';
import { useRedux } from 'hooks/useRedux';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { ReflectionSaveState } from 'typings';
import { usePermission } from 'hooks/usePermission';
import { ReflectContext } from '../Reflect';
import Card from '../components/OverviewCard/OverviewCard';
import ProjectLockModal from '../components/ProjectLockModal/ProjectLockModal';
import ReviewOptionsCard from '../components/ReviewOptionsCard/ReviewOptionsCard';
import ReviewerSelectModal from '../components/ReviewerSelectModal/ReviewerSelectModal';
import './Views.module.scss';
// =========================

// make OverviewContextType
export type OverviewContextType = {
  isReflections: boolean;
  setIsReflections: React.Dispatch<React.SetStateAction<boolean>>;
  isRequested: boolean;
  setIsRequested: React.Dispatch<React.SetStateAction<boolean>>;
  projectModalIsOpen: boolean;
  setProjectModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  reviewerModalIsOpen: boolean;
  setReviewerModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isLockedProject: boolean;
  setIsLockedProject: React.Dispatch<React.SetStateAction<boolean>>;
  isProjectAdmin: boolean;
  save: (state: ReflectionSaveState) => void;
  saveState: ReflectionSaveState;
  setSaveState: React.Dispatch<React.SetStateAction<ReflectionSaveState>>;
  user: UserAuthenticationEntity | undefined;
  isExtern: boolean;
  projectIsLocked: boolean;
};

export const OverviewContext = createContext({} as OverviewContextType);

export default function Overview() {
  const {
    checkpointGroups,
    wrapperRef,
    handleSave,
    project,
    allReflectionsAreCompleted,
  } = useContext(ReflectContext);

  const reflections = useRedux((state) =>
    state.reflection?.entities.allIds
      .map((id) => state.reflection?.entities.byId[id])
      .filter(
        (r) =>
          r?.project_process?.project_id === project?.id &&
          r?.user?.id === state.authentication?.user?.id
      )
  );

  const dreamsReflections = reflections?.flatMap((r) =>
    r?.dreams ? r.dreams.flatMap((dream) => dream?.id ?? []) : []
  );

  const dreamsFromReflections = [...new Set(dreamsReflections)];

  const lastReflectionId = // go through reflections and get the last reflection id to save the dream
    // as dreams can only be saved to a single reflection
    reflections?.reduce(
      (prev: number | undefined, curr: ReflectionEntity | undefined) => {
        if (!prev) return curr?.id;
        if (curr?.id! > prev) return curr?.id;
        return prev;
      },
      0
    ) ?? 0;

  const assessments = useRedux((state) =>
    state.assessment.entities.allIds.map(
      (id) => state.assessment.entities.byId[id]
    )
  );

  const projectIsLocked = project?.locked_for_assessment !== 0;
  const [isLockedProject, setIsLockedProject] = useState(projectIsLocked);
  const [projectModalIsOpen, setProjectModalIsOpen] = useState(false);
  const [reviewerModalIsOpen, setReviewerModalIsOpen] = useState(false);
  const [isReflections, setIsReflections] = useState(false);
  const [isRequested, setIsRequested] = useState(
    !!(assessments?.find(
      (a) => a?.project_id === project?.id
    ) as AssessmentEntity)
  );

  const [saveState, setSaveState] = useState<ReflectionSaveState>(
    {} as ReflectionSaveState
  );

  const [dreamState, setDreamState] = useState<number[]>(dreamsFromReflections);

  const user = useRedux((state) => state.authentication?.user);

  const isProjectAdmin = project?.members?.find(
    (member) =>
      member.user_id === user?.id &&
      (member.membership === 'admin' || member.membership === 'owner')
  );

  const save = (state: ReflectionSaveState) => handleSave(state, setSaveState);

  useEffect(() => {
    setIsLockedProject(!!project?.locked_for_assessment);
  }, [project?.locked_for_assessment]);

  const isExtern = usePermission(['Extern']);

  useEffect(() => {
    const reflectionsToAdd = reflections
      ?.filter((reflection) =>
        reflection?.dreams?.some((dream) => !dreamState.includes(dream.id))
      )
      .map((reflection) => reflection?.id);

    // if the dream was saved during a reflection and student wants to remove it
    if (reflectionsToAdd?.length) {
      // Update the saveState and trigger save function for each reflection ID
      reflectionsToAdd?.forEach((reflectionId) => {
        setSaveState((prev) => ({
          ...prev,
          dreams: dreamState,
          result: 0,
          id: reflectionId,
        }));
        save({ ...saveState, dreams: dreamState, result: 0, id: reflectionId });
      });
    } else {
      // if the student adds a dream that was not saved during a reflection
      setSaveState((prev) => ({
        ...prev,
        dreams: dreamState,
        result: 0,
        // we use the last reflection process id to save the dream
        id: lastReflectionId,
      }));
      save({
        ...saveState,
        dreams: dreamState,
        result: 0,
        id: lastReflectionId,
      });
    }
  }, [dreamState]);

  useEffect(() => {
    if (isExtern) {
      setIsReflections(true);
    }
  }, [isExtern]);

  const contextValue = {
    isReflections,
    setIsReflections,
    isRequested,
    setIsRequested,
    projectModalIsOpen,
    setProjectModalIsOpen,
    reviewerModalIsOpen,
    setReviewerModalIsOpen,
    isLockedProject,
    setIsLockedProject,
    isProjectAdmin,
    save,
    saveState,
    setSaveState,
    user,
    isExtern,
    projectIsLocked,
  };

  useEffect(() => {
    if (!allReflectionsAreCompleted) {
      setIsReflections(true);
    }
  }, [allReflectionsAreCompleted]);

  return (
    <OverviewContext.Provider value={{ ...contextValue }}>
      <div styleName="wrapper" ref={wrapperRef}>
        {!isReflections ? (
          <>
            <ReviewOptionsCard
              type="dreams"
              dreamSelectModal={
                <DreamSelectModal
                  selected={dreamState}
                  handleSubmit={(dreams) => {
                    setDreamState(dreams);
                  }}
                />
              }
            />
            <ReviewOptionsCard type="reflection" />
            {isProjectAdmin && <ReviewOptionsCard type="project" />}
            <ReviewOptionsCard type="review" />
          </>
        ) : (
          <>
            {checkpointGroups.map(
              (c, i) => !!c.length && <Card key={i} activities={c} />
            )}
          </>
        )}
        <GridFiller />
      </div>
      {/* Locked Project Modal */}
      <AnimatePresence>
        {!!projectModalIsOpen && <ProjectLockModal />}
      </AnimatePresence>
      {/* Reviewer Select Modal */}
      <AnimatePresence>
        {!!reviewerModalIsOpen && (
          <ReviewerSelectModal projectId={project?.id} />
        )}
      </AnimatePresence>
    </OverviewContext.Provider>
  );
}
